import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaChalkboardTeacher,
  FaUserGraduate,
  FaBookOpen,
  FaLightbulb,
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';
import Logo from "../asset/logo.png";
import SummaryApi from '../common/index';

const AdminSVG = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="mx-auto mb-2"
  >
    <circle cx="32" cy="18" r="10" fill="#1E3A8A" />
    <rect x="22" y="14" width="8" height="6" stroke="#fff" strokeWidth="2" fill="none" rx="2" />
    <rect x="34" y="14" width="8" height="6" stroke="#fff" strokeWidth="2" fill="none" rx="2" />
    <line x1="30" y1="17" x2="34" y2="17" stroke="#fff" strokeWidth="2" />
    <path d="M24 24 Q32 30 40 24" stroke="#fff" strokeWidth="2" fill="transparent" />
    <rect x="22" y="28" width="20" height="26" fill="#1E3A8A" rx="6" />
    <polygon
      points="32,32 36,44 28,44"
      fill="#BFDBFE"
      style={{ animation: 'tieMove 4s infinite alternate ease-in-out' }}
    />
    <style>{`
      @keyframes tieMove {
        0% { transform: translateY(0); }
        100% { transform: translateY(4px); }
      }
    `}</style>
  </svg>
);

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminLogin = location.pathname === '/admin-panel/admin-login';

  const features = [
    {
      icon: <FaChalkboardTeacher className="text-5xl text-blue-300" />,
      title: 'Expert Educators',
      desc: 'Personalized coaching from certified faculty.'
    },
    {
      icon: <FaUserGraduate className="text-5xl text-blue-300" />,
      title: 'Student Achievements',
      desc: 'Proven academic success stories.'
    },
    {
      icon: <FaBookOpen className="text-5xl text-blue-300" />,
      title: 'Digital Notes',
      desc: 'Access rich content & mock tests anytime.'
    },
    {
      icon: <FaLightbulb className="text-5xl text-blue-300" />,
      title: 'Smart Learning',
      desc: 'Creative methods, live sessions & insights.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) =>
        prev === features.length - 1 ? 0 : prev + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [features.length]);

  const handleDotClick = (index) => {
    setCurrentFeature(index);
  };

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      toast.error("Please enter both username and password!");
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(SummaryApi.AdminLogin.url, {
        method: SummaryApi.AdminLogin.method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_name: username.trim(),
          password: password.trim()
        })
      });

      const data = await response.json();

      if (data.status) {
        toast.success("Login successful!", { autoClose: 2000 });

        // Save admin data in sessionStorage
        const adminData = { 
          admin_id: data.admin_id,
          username: data.user_name || username, 
          role: "admin",
          token: data.token || null
        };
        sessionStorage.setItem("adminData", JSON.stringify(adminData));
        sessionStorage.setItem("adminUser", adminData.username);

        // Check for redirect query parameter
        const query = new URLSearchParams(location.search);
        const redirectTo = query.get("redirect") || "/admin-panel/dashboard";
        navigate(decodeURIComponent(redirectTo));
      } else {
        toast.error(data.message || "Invalid username or password!", { autoClose: 3000 });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Network error. Please try again.", { autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = username.trim() !== '' && password.trim() !== '';

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && isFormValid && !isLoading) handleLogin();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFormValid, isLoading, username, password]);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen font-san">
      {/* Left Panel - Features */}
      <div className="hidden lg:flex lg:flex-1 bg-blue-900 text-white p-4 lg:p-10 flex-col relative">
        <div className="flex-1 flex flex-col justify-center">
          <div className="transition-all duration-500 ease-in-out space-y-4">
            <div>{features[currentFeature].icon}</div>
            <h2 className="text-2xl lg:text-3xl font-extrabold font-grotesk">
              {features[currentFeature].title}
            </h2>
            <p className="text-base lg:text-lg opacity-90">
              {features[currentFeature].desc}
            </p>
          </div>

          {/* Dots */}
          <div className="flex gap-3 mt-6 lg:mt-10">
            {features.map((_, index) => (
              <span
                key={index}
                onClick={() => handleDotClick(index)}
                className={`h-2 w-8 rounded-full cursor-pointer transition-all duration-300 ${
                  currentFeature === index
                    ? 'bg-white'
                    : 'bg-blue-400 opacity-70'
                }`}
              />
            ))}
          </div>
        </div>

        <svg
          className="absolute top-0 right-0 h-full w-32"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          fill="white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 0 C 30 50 70 50 100 100 L 100 0 Z" />
        </svg>
      </div>

      {/* Right Panel - Login */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 md:p-6 lg:p-10">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_4px_20px_rgba(30,58,138,0.2)] p-8" style={{ minHeight: '500px' }}>
          {/* Logo */}
          <div className="flex items-center justify-center mb-6 space-x-3">
            <img src={Logo} alt="Logo" className="h-12 md:h-14 object-contain" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-blue-900 text-center mb-6">
            ADMIN LOGIN
          </h1>

          <div className="space-y-6">
            {/* Username */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaUser className="text-gray-400" />
              </div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                placeholder="Username"
                autoFocus
              />
            </div>

      {/* Password */}
<div className="relative">
  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
    <FaLock className="text-gray-400" />
  </div>
  <input
    type={showPassword ? 'text' : 'password'}
    value={password}
    onChange={(e) => setPassword(e.target.value)}
    className="w-full pl-10 pr-10 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
    placeholder="Password"
  />
  <button
    type="button"
    className="absolute inset-y-0 right-0 pr-3 flex items-center justify-center text-gray-400 hover:text-blue-800"
    onClick={() => setShowPassword(!showPassword)}
  >
    {showPassword ? <FaEyeSlash /> : <FaEye />}
  </button>
</div>

            {/* Login Button */}
            <button
              onClick={handleLogin}
              disabled={!isFormValid || isLoading}
              className={`w-full py-3 rounded-lg font-semibold text-white flex justify-center items-center transition ${
                isFormValid && !isLoading
                  ? 'bg-blue-600 hover:bg-blue-800'
                  : 'bg-blue-300 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </div>
              ) : (
                'Login as Admin'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;